import type { IconAliases } from 'vuetify'
import { aliases as baseAliases } from './materialSymbols'

// Nocal-specific icon aliases with nci prefix
// These extend the base material symbol aliases
const nocalAliases: IconAliases = {
  ...baseAliases,
  // Misc.
  nciHistory: 'history',
  nciOffline: 'cloud_alert',
  nciOfflineMode: 'hard_drive',
  nciHighVisibility: 'brightness_5',
  nciSearch: 'search',
  nciInbox: 'mail',
  nciMobileHome: 'side_navigation',
  nciContinue: 'arrow_right_alt',
  nciMention: 'alternate_email',
  nciAlert: 'warning',
  nciAlertCircle: 'error',
  nciQuestionMark: 'question_mark',
  nciInfo: 'info',
  nciEmployeeManager: 'supervisor_account',
  nciPeopleGroup: 'groups',
  nciGlobe: 'globe',
  nciSocial: 'communities',
  nciTimeOptimizer: 'safety_check',
  nciNetworker: 'graph_5',
  nciPersonRunning: 'sprint',
  nciLightbulb: 'lightbulb',
  nciLightbulbLit: 'mdi:mdi-lightbulb-on-outline',
  nciHeart: 'favorite',
  nciTrayApp: 'toolbar',
  nciBell: 'notifications',
  nciRingingBell: 'notifications_active',
  nciCelebrate1: 'trophy',
  nciCelebrate2: 'celebration',
  nciFeedback: 'campaign',
  nciNoUpcomingMeetingsVibe: 'sentiment_calm',
  nciSidebarBlocks: 'view_sidebar',
  nciEscapeKey: 'mdi:mdi-keyboard-esc',
  nciStar: 'star',
  nciAgent: 'borg',
  nciLock: 'lock',
  nciLockOpen: 'lock_open',
  nciMobilePhone: 'mobile',
  nciSparkles: 'mdi:mdi-creation',
  nciDiamondShine: 'diamond_shine',
  nciVideocam: 'videocam',
  nciVideocamOff: 'videocam_off',
  nciVideoChat: 'video_chat',
  nciPencil: 'edit',
  nciLink: 'link',
  nciLinkOff: 'link_off',
  nciUndo: 'undo',
  nciRefresh: 'refresh',
  nciRemove: 'delete',
  nciClose: 'close',
  nciDone: 'check',
  nciCheck: 'check',
  nciCopy: 'content_copy',
  nciVisibility: 'visibility',
  nciVisibilityOff: 'visibility_off',
  nciChevronLeft: 'chevron_backward',
  nciChevronRight: 'chevron_forward',
  nciChevronDown: 'keyboard_arrow_down',
  nciChevronUp: 'keyboard_arrow_up',
  nciDotsHorizontal: 'more_horiz',
  nciDotsVertical: 'more_vert',

  // Recurrence (fall back to MDI due to lack of D-box, W-box, and no-repeat icons in Material Symbols)
  nciRepeat: 'mdi:mdi-repeat',
  nciRepeatOff: 'mdi:mdi-repeat-off',
  nciInfinity: 'all_inclusive',
  nciRepeatDaily: 'mdi:mdi-alpha-d-box',
  nciRepeatWeekly: 'mdi:mdi-alpha-w-box',

  // Preferences and Onboarding
  nciEditor: 'notes',
  nciTheme: 'routine',
  nciShortcuts: 'keyboard',
  nciAccountCircle: 'account_circle',
  nciDeveloper: 'build',
  nciCheckCircle: 'check_circle',
  nciCircle: 'circle',
  nciClock: 'alarm',
  nciClick: 'arrow_selector_tool',

  // App controls
  nciApplicationWindow: 'ad',
  nciSettings: 'settings',
  nciOpenInNew: 'open_in_new',
  nciCloseApp: 'power_settings_new',
  nciDragIndicator: 'drag_indicator',
  nciLogout: 'logout',

  // Mobile App Controls
  nciBackButton: 'arrow_back',
  nciBackButtonIos: 'arrow_back_ios',

  // Notes
  nciNote: 'article',
  nciFiles: 'mdi:mdi-file-tree',
  nciWeekNote: 'calendar_view_week',
  nciMeetingNote: 'event_note',
  nciPinned: 'keep',
  nciUnpin: 'keep_off',
  nciNewNote: 'note_add',
  nciNewNoteAlt: 'add_notes',
  nciRecentNotes: 'bolt',
  nciFolder: 'folder',
  nciAddFolder: 'create_new_folder',
  nciPlus: 'add',
  nciClean: 'mop',
  nciSpaceMenu: 'tune',
  nciUpload: 'upload',
  nciDownload: 'download',

  // Editor
  nciEditorComponents: 'widgets',
  nciFormatHeader1: 'format_h1',
  nciFormatHeader2: 'format_h2',
  nciFormatHeader3: 'format_h3',
  nciFormatParagraph: 'format_paragraph',
  nciFormatAlignLeft: 'format_align_left',
  nciFormatAlignCenter: 'format_align_center',
  nciFormatAlignRight: 'format_align_right',
  nciFormatAlignJustify: 'format_align_justify',
  nciFormatText: 'text_format',
  nciFormatFontSize: 'format_size',
  nciFormatBold: 'format_bold',
  nciFormatItalic: 'format_italic',
  nciFormatUnderline: 'format_underlined',
  nciFormatStrike: 'format_strikethrough',
  nciFormatSubscript: 'subscript',
  nciFormatSuperscript: 'superscript',
  nciFormatColors: 'palette',
  nciFormatTextColor: 'format_color_text',
  nciFormatHighlightColor: 'format_ink_highlighter',
  nciFormatCode: 'code',
  nciFormatCodeBlock: 'code_blocks',
  nciFormatBulletList: 'format_list_bulleted',
  nciFormatNumberedList: 'format_list_numbered',
  nciFormatTaskList: 'check_box',
  nciFormatCollapseableBlock: 'compress',
  nciFormatQuote: 'format_quote',
  nciFormatHorizontalRule: 'horizontal_rule',
  nciFormatTable: 'table',
  nciFormatClear: 'format_clear',
  nciTableCellSplit: 'splitscreen_vertical_add',
  nciTableCellMerge: 'combine_columns',
  nciTableColumnBefore: 'add_column_left',
  nciTableColumnAfter: 'add_column_right',
  nciTableColumnRemove: 'delete',
  nciTableRowAbove: 'add_row_above',
  nciTableRowBelow: 'add_row_below',
  nciTableRowRemove: 'delete',
  nciTableHeaderRow: 'nc:table-header-row',
  nciTableHeaderColumn: 'nc:table-header-column',
  nciTableRemove: 'delete_forever',
  nciTextWrap: 'format_text_wrap',
  nciTextOverflow: 'format_text_overflow',

  // Events and Calendar
  nciToday: 'sunny',
  nciSun: 'wb_sunny',
  nciMoon: 'dark_mode',
  nciAutoTheme: 'contrast', // half light / half dark — reads as "follows system"
  nciShowBackgroundCalendars: 'circle',
  nciCalendar: 'calendar_month',
  nciCalendarCheck: 'calendar_check',
  nciCalendarClock: 'calendar_clock',
  nciCalendarBlank: 'calendar_today',
  nciCalendarRemove: 'event_busy',
  nciCalendarIssue: 'event_busy',
  nciSpam: 'report', // octagon-with-alert, the Gmail-style "report spam" mark
  nciCalendarHell: 'local_fire_department', // Calendar Hell promo entry point
  nciGift: 'redeem', // "update ready" gift button (present with a bow)
  nciCalendarPlus: 'calendar_add_on',
  nciEvent: 'event',
  nciEventAdd: 'calendar_add_on',
  nciExpandCalendarLeft: 'text_select_move_back_word',
  nciCollapseCalendarRight: 'text_select_move_forward_word',
  nciReadOnly: 'lock',
  nciSendRsvp: 'send',
  nciHideRsvp: 'visibility_off',
  nciEveryoneDeclined: 'person_cancel',

  // Calendar Event
  nciEventTitle: 'title',
  nciEventTime: 'schedule',
  nciEventOrganizer: 'contact_mail',
  nciEventLocation: 'location_on',
  nciNewFolder: 'create_new_folder',
  nciPalette: 'palette',
  nciEdit: 'edit',
  nciTrash: 'delete',
  nciEditSeriesOnlyThisInstance: 'nc:layers-single',
  nciEditSeriesFutureInstances: 'mdi:mdi-layers-outline',
  nciEditSeriesEntireSeries: 'mdi:mdi-layers-triple-outline',

  // Space icons
  nciSpace: 'grid_view',
  nciWsBriefcase: 'work',
  nciWsIdea: 'lightbulb',
  nciWsCode: 'code',
  nciWsSchool: 'school',
  nciWsScience: 'science',
  nciWsPalette: 'palette',
  nciWsMusic: 'music_note',
  nciWsFitness: 'fitness_center',
  nciWsRestaurant: 'restaurant',
  nciWsFlight: 'flight',
  nciWsHome: 'home',
  nciWsGroup: 'groups',
  nciWsHeart: 'favorite',
  nciWsRocket: 'rocket_launch',
  nciWsCafe: 'local_cafe',
  nciWsBook: 'auto_stories',
  nciWsBrain: 'neurology',
  nciHeadphones: 'headphones',
  nciWsCamera: 'photo_camera',
  nciWsPets: 'pets',
  nciWsSpa: 'spa',
  nciWsBeach: 'beach_access',
  nciWsMovie: 'movie',
  nciWsGarden: 'yard',
  nciWsSports: 'sports_soccer',
  nciWsWine: 'wine_bar'
}

export { nocalAliases }
